/**
 * Same-Origin API URL Patcher
 * ---------------------------
 * Purpose:
 *   Make the deployed frontend robust to a stale or incorrect
 *   REACT_APP_BACKEND_URL bake. If the build was compiled with a URL that
 *   points to a different origin (e.g. a preview URL in a production bundle)
 *   AND the current page is served from a host whose `/api/*` endpoints are
 *   live, this patcher rewrites cross-origin `/api/*` fetches to same-origin.
 *
 * Why this matters:
 *   When a fetch goes cross-origin with `credentials: 'include'` AND the
 *   server returns `Access-Control-Allow-Origin: *`, the browser rejects the
 *   response with `TypeError: Failed to fetch`. Same-origin avoids the entire
 *   class of CORS+credentials failures.
 *
 * Behavior:
 *   - Only rewrites paths that begin with `/api/`.
 *   - Only rewrites when the baked URL host differs from window.location.host.
 *   - Verifies once that the current origin actually serves `/api/*` (probes
 *     `/api/scfr/categories` with HEAD on first call). If the probe fails, the
 *     patch is disabled and the original URL is used (preserves preview/dev).
 *   - Idempotent: only patches window.fetch once.
 *   - Falls back gracefully if anything throws.
 */

const PROBE_PATH = "/api/scfr/categories"; // a known unauthenticated GET endpoint
let probeState = "pending"; // "pending" | "ok" | "fail"
let probePromise = null;

async function probeSameOrigin() {
  if (probeState !== "pending") return probeState;
  if (!probePromise) {
    probePromise = fetch(window.location.origin + PROBE_PATH, {
      method: "GET",
      // intentionally NO credentials, NO cors headers — vanilla GET
    })
      .then((r) => {
        probeState = r.ok ? "ok" : "fail";
        return probeState;
      })
      .catch(() => {
        probeState = "fail";
        return probeState;
      });
  }
  return probePromise;
}

export function installSameOriginApiPatch() {
  if (typeof window === "undefined" || !window.fetch || window.__zwiiApiPatchInstalled) {
    return;
  }
  window.__zwiiApiPatchInstalled = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async function patchedFetch(input, init) {
    try {
      let urlStr = typeof input === "string" ? input : (input && input.url) || null;
      if (typeof urlStr === "string" && /^https?:\/\//i.test(urlStr)) {
        const u = new URL(urlStr);
        const pageHost = window.location.host;
        const isCrossOrigin = u.host !== pageHost;
        const isApiPath = u.pathname.startsWith("/api/");

        if (isCrossOrigin && isApiPath) {
          // Lazy-probe whether same-origin actually has /api
          const ok = await probeSameOrigin();
          if (ok === "ok") {
            const rewritten = window.location.origin + u.pathname + u.search;
            // Helpful one-time log for diagnosis (kept lightweight)
            if (!window.__zwiiApiPatchLogged) {
              window.__zwiiApiPatchLogged = true;
              // eslint-disable-next-line no-console
              console.info(
                "[zwii] Same-origin API patch active. Cross-origin /api/* calls will be rewritten to " +
                  window.location.origin
              );
            }
            if (typeof input === "string") {
              input = rewritten;
            } else {
              input = new Request(rewritten, input);
            }
          }
        }
      }
    } catch (_e) {
      // If anything throws, fall through to the original fetch as-is.
    }
    return originalFetch(input, init);
  };
}
